<script setup lang="ts">
import Isologo from '~/components/logos/IsoLogo.vue'
import CloverIcon from '~/components/icons/CloverIcon.vue'
import { trackResponsibleLink } from '~/tracking/responsibleGamingEvents.js'
import { getCurrentYear } from '~/utils/day-js'

defineOptions({
	name: 'Footer',
})

const route = useRoute()
const { t: $t } = useI18n()
const localePath = useLocalePath()

const linkSRIJ = 'https://www.srij.turismodeportugal.pt/pt/jogo-responsavel/como-jogar-de-forma-responsavel'
const linkICAD = 'https://www.icad.pt/ImageContent/Index/21?languageId=0'

const firstHtml = computed(() => {
	return $t(
		'Luckywise is committed to responsible gaming and recommends playing within your limits. For help and support regarding gambling issues, please visit {ICAD} or {SRIJ}.',
		{
			ICAD: `<a href="${linkICAD}" target="_blank" class="hover:underline">ICAD</a>`,
			SRIJ: `<a href="${linkSRIJ}" target="_blank" class="hover:underline">SRIJ</a>`,
		},
	)
})

const secondHtml = computed(() => {
	return $t('For support visit {SRIJ} or {SICAD}', {
		SRIJ: `<a href="${linkSRIJ}" target="_blank" class="hover:underline">SRIJ</a>`,
		SICAD: `<a href="${linkICAD}" target="_blank" class="hover:underline">SICAD</a>`,
	})
})

const isActive = (path: string) => {
	return route.path.includes(path) ? 'active' : ''
}

const sendEvent = () => {
	trackResponsibleLink({ location: 'footer' })
}

const animationBtn = 'text-white cursor-pointer flex p-0 transition-transform duration-300 ease-out hover:scale-125'
</script>

<template>
	<footer class="bg-secondary-800 pt-11 lg:pt-12 pb-6 lg:pb-12 text-neutral-50">
		<div class="container sm:space-y-11">
			<div
				class="flex flex-col sm:flex-row sm:justify-between gap-8 sm:gap-12 xl:gap-8 pb-6 divide-y divide-white/30 sm:divide-y-0"
			>
				<!-- Left column:  -->
				<div class="space-y-7 sm:max-w-96 xl:max-w-[400px]">
					<div class="flex items-start gap-4">
						<Isologo
							class="text-primary-600 shrink-0"
							:size="48"
						/>
						<p class="font-bold text-base sm:text-lg text-neutral-50">
							{{ $t('Committed to transforming the way you experience luck') }}.
						</p>
					</div>

					<div class="flex items-center gap-5">
						<NuxtLink
							to="https://www.facebook.com/people/Luckywise/61583315142948/"
							target="_blank"
							rel="noopener noreferrer"
							aria-label="Visit Luckywise360 on Facebook"
							class="inline-flex"
						>
							<UButton
								icon="i-basil:facebook-solid"
								color="white"
								variant="soft"
								size="xl"
								:ui="{ icon: { size: { xl: 'size-7' } } }"
								class="text-white cursor-pointer flex p-0"
								:class="animationBtn"
							/>
						</NuxtLink>
						<NuxtLink
							to="https://www.instagram.com/luckywise360/"
							target="_blank"
							rel="noopener noreferrer"
							aria-label="Visit Luckywise360 on Instagram"
							class="inline-flex"
						>
							<UButton
								icon="i-basil:instagram-solid"
								color="white"
								variant="soft"
								size="xl"
								:ui="{ icon: { size: { xl: 'size-7' } } }"
								class="text-white cursor-pointer flex p-0"
								:class="animationBtn"
							/>
						</NuxtLink>
						<NuxtLink
							to="https://www.youtube.com/@Luckywise-360"
							target="_blank"
							rel="noopener noreferrer"
							aria-label="Visit Luckywise360 on YouTube"
							class="inline-flex"
						>
							<UButton
								icon="i-basil:youtube-solid"
								color="white"
								variant="soft"
								size="xl"
								:ui="{ icon: { size: { xl: 'size-7' } } }"
								class="text-white cursor-pointer flex p-0"
								:class="animationBtn"
							/>
						</NuxtLink>
					</div>
					<div class="space-y-14">
						<p class="text-sm leading-6">
							{{
								$t(
									'This site is not operated by an official lottery operator. We do not sell tickets. It is solely an informational and statistical portal',
								)
							}}.
						</p>

						<div class="hidden sm:flex items-center gap-5">
							<NuxtLink
								:to="linkSRIJ"
								target="_blank"
								rel="noopener noreferrer"
								aria-label="Visit Jogo Responsavel"
								class="inline-flex"
							>
								<UButton
									icon="i-lineicons:protection"
									label="SRIJ"
									color="primary"
									class="text-success-800 font-semibold px-3 py-1"
									square
									variant="solid"
									:ui="{
										rounded: 'rounded-full',
									}"
								/>
							</NuxtLink>
							<NuxtLink
								:to="linkICAD"
								target="_blank"
								rel="noopener noreferrer"
								aria-label="Visit ICAD"
								class="inline-flex"
							>
								<UButton
									icon="i-lineicons:protection"
									label="ICAD"
									color="primary"
									class="text-success-800 font-semibold px-3 py-1"
									square
									variant="solid"
									:ui="{
										rounded: 'rounded-full',
									}"
							/></NuxtLink>

							<div
								class="border-2 border-[#FF3B30] rounded-full size-10 text-[#FF3B30] font-bold flex justify-center items-center"
							>
								18+
							</div>
						</div>
					</div>
				</div>

				<!-- Middle column: Legal Information -->
				<div class="sm:max-w-64 xl:max-w-[330px] space-y-8 lg:space-y-4">
					<h3 class="font-bold text-base leading-6 hidden sm:block">{{ $t('Legal Information') }}</h3>

					<div class="space-y-4 text-sm">
						<p class="leading-6">
							<span class="font-semibold text-primary-500">18+ | {{ $t('Play responsibly') }}. </span>
							<span v-html="firstHtml"></span>
						</p>

						<!-- <p class="leading-6">
							<span class="font-semibold">{{ $t('Affiliate Disclosure') }}:</span>
							{{
								$t(
									'Please note that some links on this site are affiliate links and, at no additional cost to you, we may earn a commission if you make a purchase through these links. This helps support our work to provide you with the best statistics and information',
								)
							}}.
						</p> -->
					</div>

					<div class="flex sm:hidden items-center gap-5 pt-1">
						<NuxtLink
							:to="linkSRIJ"
							target="_blank"
							rel="noopener noreferrer"
							aria-label="Visit Jogo Responsavel"
							class="inline-flex"
						>
							<UButton
								icon="i-lineicons:protection"
								label="SRIJ"
								color="primary"
								class="text-success-800 font-semibold px-3 py-1"
								square
								variant="solid"
								:ui="{
									rounded: 'rounded-full',
								}"
							/>
						</NuxtLink>
						<NuxtLink
							to="https://www.icad.pt/ImageContent/Index/21?languageId=0"
							target="_blank"
							rel="noopener noreferrer"
							aria-label="Visit ICAD"
							class="inline-flex"
						>
							<UButton
								icon="i-lineicons:protection"
								label="ICAD"
								color="primary"
								class="text-success-800 font-semibold px-3 py-1"
								square
								variant="solid"
								:ui="{
									rounded: 'rounded-full',
								}"
						/></NuxtLink>

						<div
							class="border-2 border-[#FF3B30] rounded-full size-10 text-[#FF3B30] font-bold flex justify-center items-center"
						>
							18+
						</div>
					</div>
				</div>

				<!-- Right column: Useful Links -->
				<div class="space-y-8 lg:space-y-4 sm:hidden xl:block">
					<h3 class="font-bold text-base hidden lg:block">{{ $t('Useful Links') }}</h3>

					<ul class="space-y-6 text-sm text-center lg:text-start">
						<li class="group">
							<NuxtLink
								:to="localePath('/responsible-gaming')"
								class="flex justify-center lg:justify-start items-center gap-2 text-neutral-50 group-hover:text-opacity-80"
								:class="isActive('/responsible-gaming')"
								@click="sendEvent"
							>
								<CloverIcon class="size-5" />
								<span>{{ $t('Responsible Playing') }}</span>
							</NuxtLink>
						</li>
						<li>
							<NuxtLink
								:to="localePath('/terms-of-service')"
								:class="isActive('/terms-of-service')"
								class="text-neutral-50 hover:text-opacity-80"
							>
								{{ $t('Terms of Service') }}
							</NuxtLink>
						</li>
						<li>
							<NuxtLink
								:to="localePath('/privacy-policy')"
								:class="isActive('/privacy-policy')"
								class="text-neutral-50 hover:text-opacity-80"
							>
								{{ $t('Privacy Policy') }}
							</NuxtLink>
						</li>
						<li>
							<NuxtLink
								:to="localePath('/cookies')"
								:class="isActive('/cookies')"
								class="text-neutral-50 hover:text-opacity-80"
							>
								Cookies
							</NuxtLink>
						</li>
					</ul>
				</div>
			</div>
			<div class="space-y-8 lg:space-y-4 hidden sm:block xl:hidden">
				<h3 class="font-bold text-base">{{ $t('Useful Links') }}</h3>

				<ul class="text-sm flex gap-5">
					<li class="group">
						<NuxtLink
							:to="localePath('/responsible-gaming')"
							class="flex justify-center lg:justify-start items-center gap-2 text-neutral-50 group-hover:text-opacity-80"
							:class="isActive('/responsible-gaming')"
							@click="sendEvent"
						>
							<CloverIcon class="size-5" />
							<span>{{ $t('Responsible Playing') }}</span>
						</NuxtLink>
					</li>
					<li>
						<NuxtLink
							:to="localePath('/terms-of-service')"
							:class="isActive('/terms-of-service')"
							class="text-neutral-50 hover:text-opacity-80"
						>
							{{ $t('Terms of Service') }}
						</NuxtLink>
					</li>
					<li>
						<NuxtLink
							:to="localePath('/privacy-policy')"
							:class="isActive('/privacy-policy')"
							class="text-neutral-50 hover:text-opacity-80"
						>
							{{ $t('Privacy Policy') }}
						</NuxtLink>
					</li>
					<li>
						<NuxtLink
							:to="localePath('/cookies')"
							:class="isActive('/cookies')"
							class="text-neutral-50 hover:text-opacity-80"
						>
							Cookies
						</NuxtLink>
					</li>
				</ul>
			</div>
			<div
				class="border-t border-white/30 pt-8 lg:pt-11 flex flex-col sm:flex-row items-center justify-between gap-7 text-xs sm:text-sm text-center sm:text-left"
			>
				<p class="font-semibold sm:font-normal text-white">
					© {{ getCurrentYear() }} Luckywise360. {{ $t('All rights reserved') }}.
				</p>

				<p class="text-primary-500 font-semibold max-w-60 smm:max-w-full mx-0">
					18+ | {{ $t('Play responsibly') }}. <span v-html="secondHtml"></span>.
				</p>
			</div>
		</div>
	</footer>
</template>

<style lang="postcss" scoped>
.footer {
	.border-bottom {
		background: rgba(255, 255, 255, 0.32);
	}
}

.active {
	@apply text-primary-500 group-hover:text-primary-500/80  transition-all duration-300 font-bold;
}
</style>
